import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Signal {
  id: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  created_at: string;
}

interface Approval {
  id: string;
  type: string;
  subject: string;
  status: 'pending' | 'approved' | 'rejected';
}

export default function CommandCentre() {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError('');
      const [sigRes, appRes] = await Promise.all([
        api.get('/api/command-centre/signals'),
        api.get('/api/command-centre/approvals'),
      ]);
      setSignals(sigRes.data || []);
      setApprovals(appRes.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(id: string) {
    try {
      await api.post(`/api/command-centre/approvals/${id}/approve`);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleReject(id: string) {
    try {
      await api.post(`/api/command-centre/approvals/${id}/reject`);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  }

  function severityColor(severity: string) {
    switch (severity) {
      case 'critical':
        return '#dc2626';
      case 'warning':
        return '#d97706';
      default:
        return '#2563eb';
    }
  }

  return (
    <div>
      <h1 style={styles.heading}>Command Centre</h1>
      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <h2 style={styles.subheading}>Signals</h2>
          <div style={styles.card}>
            {signals.length === 0 && <div style={styles.empty}>No signals</div>}
            {signals.map((s) => (
              <div key={s.id} style={styles.row}>
                <div>
                  <span
                    style={{
                      ...styles.badge,
                      background: severityColor(s.severity) + '15',
                      color: severityColor(s.severity),
                    }}
                  >
                    {s.severity}
                  </span>
                  <div style={styles.rowTitle}>{s.type}</div>
                  <div style={styles.rowMeta}>{s.message}</div>
                </div>
                <div style={styles.rowDate}>{new Date(s.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>

          <h2 style={{ ...styles.subheading, marginTop: 24 }}>Pending Approvals</h2>
          <div style={styles.card}>
            {approvals.length === 0 && <div style={styles.empty}>No pending approvals</div>}
            {approvals.map((a) => (
              <div key={a.id} style={{ ...styles.row, alignItems: 'center' }}>
                <div>
                  <div style={styles.rowTitle}>{a.type}</div>
                  <div style={styles.rowMeta}>{a.subject}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleApprove(a.id)} style={{ ...styles.button, background: '#16a34a' }}>
                    Approve
                  </button>
                  <button onClick={() => handleReject(a.id)} style={{ ...styles.button, background: '#dc2626' }}>
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12 },
  subheading: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  card: { background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' },
  empty: { padding: 20, color: '#9ca3af' },
  row: { padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' },
  rowTitle: { fontWeight: 500, fontSize: 14 },
  rowMeta: { fontSize: 12, color: '#6b7280' },
  rowDate: { fontSize: 12, color: '#9ca3af' },
  badge: { display: 'inline-block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, marginBottom: 4 },
  button: { padding: '6px 12px', fontSize: 12, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
};
